import type { SwitchChangeFn } from '../../../components/Switch/Switch';

import { useStore } from '@tanstack/react-form';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

import { getTestProps } from '../../../lib/helpers';

import HelpErrorTextsTemplate from '../../../components/HelpErrorTextsTemplate/HelpErrorTextsTemplate';
import RequiredTemplate from '../../../components/RequiredTemplate/RequiredTemplate';
import Switch from '../../../components/Switch/Switch';

import { useFieldContext, useFormContext } from '../../form';
import { useFieldErrors } from '../../useFieldErrors';

interface ActionsFieldProps {
  /**
   * If field is disabled
   */
  disabled?: boolean;
  /**
   * Test ID for the component
   */
  testId?: string;
}

const ActionsField = ({ disabled, testId }: ActionsFieldProps) => {
  const { t } = useTranslation();

  const field = useFieldContext<{ action: string }[]>();
  const form = useFormContext();
  const errors = useFieldErrors(field.name);

  const webhookType = useStore(form.store, (state) => state.values.type);

  const actionsOptions = useMemo(() => {
    const actions: {
      id: string;
      name: string;
      label: string;
      describe?: string;
    }[] = [
      { id: 'create', name: 'Create', label: t('Global.Create') },
      { id: 'update', name: 'Update', label: t('Global.Update') },
      { id: 'delete', name: 'Delete', label: t('Global.Delete') },
    ];

    if (webhookType !== 'sync') {
      actions.push(
        {
          id: 'publish',
          name: 'Publish',
          label: t('ObjectStatus.Transition.publish'),
        },
        {
          id: 'unpublish',
          name: 'Unpublish',
          label: t('ObjectStatus.Transition.unpublish'),
        },
        {
          id: 'archive',
          name: 'Archive',
          label: t('ObjectStatus.Transition.archive'),
        },
        {
          id: 'custom',
          name: 'Custom',
          label: t('Global.Custom'),
          describe: t('Webhooks.CustomActionDescription'),
        },
      );
    }

    return actions;
  }, [t, webhookType]);

  const actionsMap = useMemo(
    () => (field.state.value || []).map(({ action }) => action),
    [field.state.value],
  );

  const handleChangeActions = useCallback<SwitchChangeFn>(
    (e) => {
      if (e.target.value) {
        field.setValue((value) => {
          return [
            ...value,
            {
              action: e.target.name,
            },
          ];
        });
      } else {
        field.setValue((value) => [
          ...value.filter(({ action }) => action !== e.target.name),
        ]);
      }
    },
    [field],
  );

  return (
    <>
      <label
        className="block text-sm text-slate-400 mb-2"
        {...getTestProps(testId, `label`)}
      >
        {t('Webhooks.Actions')} <RequiredTemplate />
      </label>

      <div
        className={twMerge(
          'rounded-lg',
          errors ? 'border border-solid border-red' : 'mb-4',
        )}
        {...getTestProps(testId, `container`)}
      >
        {actionsOptions.map((el) => (
          <div className={'mb-3'} key={el.id}>
            <Switch
              label={el.label}
              name={el.name}
              checked={actionsMap?.indexOf(el.name) > -1}
              onChange={handleChangeActions}
              disabled={disabled}
              {...getTestProps(testId, `switch-${el.id}`, 'testId')}
            />
            {el.describe && (
              <span
                className="flex text-sm ml-14 max-w-[320px] dark:text-slate-200"
                {...getTestProps(testId, `switch-describe-${el.id}`)}
              >
                {el.describe}
              </span>
            )}
          </div>
        ))}
      </div>

      {errors && (
        <HelpErrorTextsTemplate
          error={errors}
          {...getTestProps(testId, '', 'testId')}
        />
      )}
    </>
  );
};

export default ActionsField;
